import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, MapPin, Video } from "lucide-react";
import { EASE, MaskedLine, Overline } from "./Reveal";
import { IMAGES, WHATSAPP_URL, scrollToId, trackClick } from "../../lib/site";

export const Hero = () => {
  const { scrollY } = useScroll();
  const yImage = useTransform(scrollY, [0, 600], [0, 90]);
  const yDecor = useTransform(scrollY, [0, 600], [0, -60]);

  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative overflow-hidden pt-32 pb-20 lg:pt-40 lg:pb-28"
    >
      <motion.div
        style={{ y: yDecor }}
        className="pointer-events-none absolute -top-24 -right-24 h-[420px] w-[420px] rounded-full bg-clay/15 blur-3xl"
      />
      <div className="pointer-events-none absolute top-1/2 -left-40 h-[380px] w-[380px] rounded-full bg-brand/10 blur-3xl" />

      <div className="relative mx-auto grid max-w-7xl grid-cols-1 items-center gap-14 px-6 lg:grid-cols-12 lg:gap-8 lg:px-10">
        <div className="lg:col-span-7">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: EASE }}
          >
            <Overline>
              Psicóloga · Terapia Sistêmica Familiar · Online para todo o Brasil
            </Overline>
          </motion.div>

          <h1 className="mt-8 font-serif text-5xl font-medium leading-[1.02] tracking-tight text-bark sm:text-6xl lg:text-7xl">
            <MaskedLine delay={0.15}>Um espaço seguro</MaskedLine>
            <MaskedLine delay={0.3}>para reconstruir a sua</MaskedLine>
            <MaskedLine delay={0.45} className="italic text-brand">
              saúde mental.
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subheadline"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.75, ease: EASE }}
            className="mt-8 max-w-xl text-base leading-relaxed text-moss sm:text-lg"
          >
            Psicoterapia online com Terapia Sistêmica Familiar para quem enfrenta
            exaustão emocional, conflitos familiares ou os desafios de uma doença
            crônica.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.9, ease: EASE }}
            className="mt-10 flex flex-wrap items-center gap-x-8 gap-y-5"
          >
            <a
              data-testid="hero-primary-cta"
              href={WHATSAPP_URL}
              onClick={() => trackClick("hero")}
              target="_blank"
              rel="noopener noreferrer"
              className="group inline-flex items-center gap-3 rounded-full bg-brand px-10 py-5 text-base font-semibold text-sand shadow-[0_16px_36px_rgba(58,77,57,0.28)] transition-[background-color,transform,box-shadow] duration-300 hover:-translate-y-1 hover:bg-brand-deep hover:shadow-[0_20px_44px_rgba(58,77,57,0.34)]"
            >
              Agendar sessão
              <span className="h-2 w-2 rounded-full bg-wa transition-transform duration-300 group-hover:scale-125" />
            </a>
            <button
              data-testid="hero-secondary-cta"
              onClick={() => scrollToId("#abordagem")}
              className="group inline-flex items-center gap-2 text-sm font-semibold text-brand underline decoration-clay/60 decoration-2 underline-offset-8 transition-colors duration-300 hover:text-brand-deep"
            >
              Entenda como funciona
              <ArrowDown className="h-4 w-4 transition-transform duration-300 group-hover:translate-y-0.5" />
            </button>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 1, delay: 1.15 }}
            className="mt-8 flex flex-wrap gap-x-6 gap-y-2 text-xs font-medium text-moss"
          >
            <span className="inline-flex items-center gap-1.5">
              <Video className="h-3.5 w-3.5 text-copper" /> Online para todo o Brasil
            </span>
            <span className="inline-flex items-center gap-1.5">
              <MapPin className="h-3.5 w-3.5 text-copper" /> Presencial em São José do Rio Preto/SP
            </span>
          </motion.div>
        </div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 40, scale: 0.96 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            transition={{ duration: 1.2, delay: 0.5, ease: EASE }}
            className="relative mx-auto max-w-md"
          >
            <div className="overflow-hidden rounded-t-[999px] rounded-b-[2rem] shadow-[0_32px_64px_rgba(45,51,47,0.14)]">
              <motion.img
                style={{ y: yImage, scale: 1.15 }}
                src={IMAGES.hero}
                alt="Vanessa Calsavara, psicóloga, em seu consultório"
                className="aspect-[5/6] w-full object-cover object-center"
                data-testid="hero-image"
              />
            </div>
            <div className="animate-float-soft absolute -bottom-6 -left-8 rounded-2xl bg-white/90 px-6 py-4 shadow-[0_20px_40px_rgba(45,51,47,0.1)] backdrop-blur-md">
              <p className="font-serif text-3xl font-medium text-brand">8+</p>
              <p className="text-xs font-semibold uppercase tracking-[0.16em] text-moss">
                anos de experiência
                <br />
                clínica
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
